import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from '@tyro/i18n';
import type { JSX } from 'react';
import { SearchFavoriteIcon } from '../favourite-icon';
import { SearchOption } from '../option';
import { type SearchOptionData, useSearchProvider } from '../provider';
import { SectionContainer } from '../section-container';

interface PageSectionProps {
  pages: {
    partyId: string;
    type: 'PAGE';
    text: string;
    meta: {
      path: string;
      icon: JSX.Element;
      breadcrumbs: string[];
    };
  }[];
}

export function PageOption({
  option,
  endIcon,
}: {
  option: SearchOptionData;
  endIcon?: JSX.Element;
}) {
  const {
    partyId,
    text,
    meta: { path, icon, breadcrumbs },
  } = option as PageSectionProps['pages'][number];

  return (
    <SearchOption
      key={partyId}
      path={path}
      optionData={{ ...option, meta: { path, breadcrumbs } }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'text.secondary',
            }}
          >
            {icon}
          </Box>
          <Stack>
            <Typography component="span" variant="body2">
              {text}
            </Typography>
            <Typography component="span" variant="caption">
              {breadcrumbs.join(' > ')}
            </Typography>
          </Stack>
        </Stack>
        {endIcon}
      </Stack>
    </SearchOption>
  );
}

export function PagesSection({ pages }: PageSectionProps) {
  const { t } = useTranslation(['common']);
  const { favouriteIds, toggleFavorite } = useSearchProvider();
  if (!pages || pages.length === 0) return null;

  return (
    <SectionContainer heading={t('common:pages')}>
      {pages.map((option) => {
        const isFavorite = favouriteIds.has(option.partyId);
        return (
          <PageOption
            key={option.partyId}
            option={option}
            endIcon={
              <SearchFavoriteIcon
                isFavorite={isFavorite}
                onClick={() =>
                  toggleFavorite(option.partyId, {
                    ...option,
                    meta: {
                      ...option.meta,
                      // @ts-expect-error
                      icon: undefined,
                    },
                  })
                }
              />
            }
          />
        );
      })}
    </SectionContainer>
  );
}
